/**
 * Derive the portion of a payment that counts toward booking/proposal balance (excludes card surcharge and tips).
 * Handles inconsistent rows (e.g. base_amount 0 with positive amount, or trigger-set base = full invoice while amount = deposit).
 */

export function isPaymentCountedForBalance(
  status: string | undefined | null,
): boolean {
  const s = String(status || '').toLowerCase();
  return s === 'completed' || s === 'succeeded' || s === 'paid';
}

export function paymentPrincipal(p: {
  amount?: number | null;
  base_amount?: number | null;
  surcharge_amount?: number | null;
  tip_amount?: number | null;
}): number {
  const amt = Number(p.amount) || 0;
  const sur = Number(p.surcharge_amount) || 0;
  const tip = Number(p.tip_amount) || 0;
  const fromGross = Math.max(0, Math.round((amt - sur - tip) * 100) / 100);

  const storedRaw = p.base_amount;
  const storedBase =
    storedRaw != null && storedRaw !== '' && Number.isFinite(Number(storedRaw))
      ? Number(storedRaw)
      : NaN;

  // When amount matches principal + surcharge + tip, trust that split (covers deposits, bad base_amount, full-invoice base with partial amount).
  if (amt > 0.005) {
    const sumParts = Math.round((fromGross + sur + tip) * 100) / 100;
    if (Math.abs(amt - sumParts) <= 0.02) {
      return fromGross;
    }
  }

  if (!Number.isFinite(storedBase)) {
    return fromGross;
  }

  if (sur > 0 && amt > 0) {
    const reconstructed = Math.round((storedBase + sur + tip) * 100) / 100;
    if (Math.abs(amt - reconstructed) <= 0.02) return storedBase;
    return fromGross;
  }

  // No surcharge: stored base larger than amount (e.g. trigger set booking total) — trust gross minus tip.
  if (storedBase > amt + 0.02 && amt > 0.005) {
    return fromGross;
  }

  // Stored base zero but customer was charged — do not report $0 principal.
  if (storedBase <= 0.005 && amt > 0.005) {
    return fromGross;
  }

  return storedBase;
}

/** Total charged on the payment row (customer-facing), excluding tips which are tracked separately. */
export function paymentGross(p: { amount?: number | null }): number {
  return Number(p.amount) || 0;
}
